/* eslint-disable no-alert */
import React, { useState, useEffect, useContext } from 'react';
import {navigate} from "@reach/router";

import JobListContext from '../../context/jobListContext';

import DropDown from '../../../components/dropdown';
import SlaGraph from '../../../components/slaGraph';
import IReactiveJob from '../../interfaces/reactiveJob';
import filterHelper from "../../../hooks/useFilterHelper";


import {computeSlaSummary, slaPassed, slaStatsFromCounts, slaUpcoming} from "./helper";
import { ReactiveCallSlaSummary, SlaCounts } from "../../../functions/GetReactiveCallSlaSummaryFunction";
import { Divider, DropdownCtn, HeadingCtn, HeadingDropdownCtn, GraphCtn, SummaryList } from './index.style';
import moment from "moment";

interface ServiceProps {
  reactiveJobList: IReactiveJob[];

  // True while the counts are still loading: every count is drawn as a pale placeholder block.
  pending?: boolean;

  // The database's counts (PORTAL-199). Undefined means the counts are taken from reactiveJobList in the browser, as
  // they always were; a value, or null while it is still to come, means they come from the database and the list is
  // not looked at. Null leaves the placeholders in place rather than showing numbers there are none of.
  summary?: ReactiveCallSlaSummary | null;
}

const SlaIndictorSum: React.FC<ServiceProps> = ({ reactiveJobList, pending = false, summary }): React.ReactElement => {
  const { filtersReactive,updateReactiveFilters } = useContext(JobListContext);
  const [ period, setPeriod ] = useState({
    startDate: moment(),
    endDate: moment().add(7,'day')
  });
  const [slaStatistics, setSLAStatistics] = useState({
    upcoming: slaUpcoming,
    onsite: slaPassed,
    actual: slaPassed,
  });

  // The period the dropdown shows, kept so counts that arrive after a change are drawn for that period.
  const [ selectedPeriod, setSelectedPeriod ] = useState('thisMonth');

  const { filterByDate, getDatePeriod } = filterHelper();

  const updateData = (newDate: string) => {
    if(summary !== undefined) {
      const counts = summary !== null ? (summary.counts as Record<string, SlaCounts | undefined>)[newDate] : undefined;

      if(counts !== undefined) {
        setSLAStatistics(slaStatsFromCounts(counts));
      }
      return;
    }

    let newJobList = filterByDate(newDate,reactiveJobList,'reactive');

    if(reactiveJobList.length > 0) {
      setSLAStatistics({
        upcoming: computeSlaSummary(newJobList, 'upcoming'),
        onsite: computeSlaSummary(newJobList, 'onsite'),
        actual: computeSlaSummary(newJobList, 'complete')
      })
    }
  }

  useEffect(() => {
    updateData(summary !== undefined ? selectedPeriod : 'thisMonth');
  },[reactiveJobList, summary]);

  const handleChange = (newValue: any) => {
    setSelectedPeriod(newValue);
    updateData(newValue);
    let datePeriod = getDatePeriod(newValue);
    setPeriod(datePeriod);
  };

  const handleClick = (sla: any) => {
    navigate('/app/reactiveMaintenance');
    updateReactiveFilters({
      ...filtersReactive,
      slaFilters: sla,
      dateFilter: period,
    })
  };

  return (
    <React.Fragment>
      <HeadingCtn>
        <HeadingDropdownCtn>
          <span>SLA Indicator summary</span>
          <DropdownCtn>
            <span>Show results for</span>
            <div>
              <DropDown defaultVal={1} optionList="dateFilter" onChange={handleChange} />
            </div>
          </DropdownCtn>
        </HeadingDropdownCtn>
      </HeadingCtn>
      <SummaryList>
        <GraphCtn>
          <span>UPCOMING JOBS</span>
          <div>
            {Object.keys(slaStatistics.upcoming).map(status => {
              if(status == 'total') return;
              return <SlaGraph
                title={slaStatistics.upcoming[status].title}
                maxCount={slaStatistics.upcoming.total}
                count={slaStatistics.upcoming[status].count}
                pending={pending}
                primaryColor={slaStatistics.upcoming[status].primaryColor}
                secondaryColor={slaStatistics.upcoming[status].secondaryColor}
                onClick={() => handleClick(status)}
              >
                {slaStatistics.upcoming[status].icon}
              </SlaGraph>
            })}
          </div>
        </GraphCtn>
        <Divider />
        <GraphCtn>
          <span>ON SITE TIME SLA</span>
          <div>
            {Object.keys(slaStatistics.onsite).map(status => {
              if(status == 'total') return;
              return <SlaGraph
                title={slaStatistics.onsite[status].title}
                maxCount={slaStatistics.onsite.total}
                count={slaStatistics.onsite[status].count}
                pending={pending}
                primaryColor={slaStatistics.onsite[status].primaryColor}
                secondaryColor={slaStatistics.onsite[status].secondaryColor}
                onClick={() => {
                  if(status === 'missedDeadline') handleClick('onsiteMissedDeadline');
                  if(status === 'doneOnTime') handleClick('onsiteDoneOnTime');
                }}
              >
                {slaStatistics.onsite[status].icon}
              </SlaGraph>
            })}
          </div>
        </GraphCtn>
        <Divider />
        <GraphCtn>
          <span>COMPLETION TIME SLA</span>
          <div>
            {Object.keys(slaStatistics.actual).map(status => {
              if (status == 'total') return;
              return <SlaGraph
                title={slaStatistics.actual[status].title}
                maxCount={slaStatistics.actual.total}
                count={slaStatistics.actual[status].count}
                pending={pending}
                primaryColor={slaStatistics.actual[status].primaryColor}
                secondaryColor={slaStatistics.actual[status].secondaryColor}
                onClick={() => {
                  if(status === 'missedDeadline') handleClick('completionMissedDeadline');
                  if(status === 'doneOnTime') handleClick('completionDoneOnTime');
                }}
              >
                {slaStatistics.actual[status].icon}
              </SlaGraph>
            })}
          </div>
        </GraphCtn>
      </SummaryList>
    </React.Fragment>
  );
};

export default SlaIndictorSum;
